import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { supabase } from '@/integrations/supabase/client';
import { logError } from "@/lib/logError";
import { STORAGE_KEYS } from "@/config";

/**
 * Hook that handles deep links for OAuth callbacks in native Capacitor apps.
 * Supports both:
 * - Implicit flow (access_token/refresh_token in hash)
 * - PKCE flow (code in query)
 * 
 * Uses custom URL scheme (com.brrgr.mobile://auth) for reliable OAuth return.
 */
export function useNativeAuth() {
  useEffect(() => {
    // Only run on native platforms
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    let listener: any;

    const setupDeepLinkHandler = async () => {
      listener = await App.addListener('appUrlOpen', async (event: URLOpenListenerEvent) => {
        console.log('[NativeAuth] Deep link received:', event.url);

        try {
          // Handle custom scheme URLs (com.brrgr.mobile://auth?code=xxx)
          // URL constructor may fail on custom schemes, so parse manually if needed
          let url: URL;
          try {
            url = new URL(event.url);
          } catch {
            // Fallback: parse custom scheme manually
            // e.g., com.brrgr.mobile://auth?code=xxx#access_token=yyy
            const schemeMatch = event.url.match(/^[^:]+:\/\/([^?#]*)\??([^#]*)#?(.*)?$/);
            if (!schemeMatch) {
              console.warn('[NativeAuth] Could not parse URL:', event.url);
              return;
            }
            // Create a fake https URL for parsing
            const fakeUrl = `https://placeholder/${schemeMatch[1]}?${schemeMatch[2] || ''}#${schemeMatch[3] || ''}`;
            url = new URL(fakeUrl);
          }

          // Supabase can return either PKCE code in query OR tokens in hash (implicit)
          const hashParams = new URLSearchParams((url.hash || '').replace('#', ''));
          const queryParams = url.searchParams;

          const code = queryParams.get('code') || hashParams.get('code');
          const accessToken = hashParams.get('access_token') || queryParams.get('access_token');
          const refreshToken = hashParams.get('refresh_token') || queryParams.get('refresh_token');

          if (code) {
            console.log('[NativeAuth] Found PKCE code, exchanging for session...');
            const { data, error } = await supabase.auth.exchangeCodeForSession(code);
            if (error) {
              logError(error, { where: "[NativeAuth] Error exchanging code" });
              // Close browser even on error so user isn't stuck
              try { await Browser.close(); } catch {}
              return;
            }
            console.log('[NativeAuth] Session set successfully:', data.user?.id);
          } else if (accessToken && refreshToken) {
            console.log('[NativeAuth] Found OAuth tokens, setting session...');

            const { data, error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });

            if (error) {
              logError(error, { where: "[NativeAuth] Error setting session" });
              try { await Browser.close(); } catch {}
              return;
            }
            console.log('[NativeAuth] Session set successfully:', data.user?.id);
          } else {
            // Check for error in the callback
            const err = hashParams.get('error') || queryParams.get('error');
            const errorDescription =
              hashParams.get('error_description') || queryParams.get('error_description');

            if (err) {
              logError(err, { where: '[NativeAuth] OAuth error', errorDescription });
            } else {
              console.warn('[NativeAuth] Deep link received but no auth payload found.');
            }
            try { await Browser.close(); } catch {}
            return;
          }

          // Store the flag that user has logged in before
          try {
            localStorage.setItem(STORAGE_KEYS.HAS_LOGGED_IN_BEFORE, 'true');
            localStorage.removeItem(STORAGE_KEYS.GUEST);
          } catch {}

          // Close the browser that was opened for OAuth
          try {
            await Browser.close();
            console.log('[NativeAuth] Browser closed');
          } catch (e) {
            console.log('[NativeAuth] Browser.close() not needed or failed:', e);
          }

          // Navigate to home - app is now in foreground
          console.log('[NativeAuth] Navigating to /home');
          window.location.href = '/home';
        } catch (err) {
          logError(err, { where: "[NativeAuth] Error processing deep link" });
          try { await Browser.close(); } catch {}
        }
      });
    };

    setupDeepLinkHandler();

    return () => {
      if (listener) {
        listener.remove();
      }
    };
  }, []);
}
